/**
 * WS-E — the React surface over `polarity.ts`, the piece D-18 deliberately deferred.
 *
 * `polarity.ts:11-17` reserved exactly this: *"The React surface — `usePolarity`,
 * `PolarityContext`, `PolarityToggle` — is WS-E's… WS-E should import `POLARITY_STORAGE_KEY`,
 * `readStoredPolarity`, `writeStoredPolarity`, `resolvePolarity` and `applyPolarityToDom` rather
 * than re-deriving any of them, so there is exactly one definition of which class goes on
 * `<html>`."* This file imports all five and derives nothing.
 *
 * ⭐ PORTED FROM `cloud/dp/shell/src/platform/{usePolarity,PolarityContext}.ts(x)` — the owner asked
 * for *that* widget. ⛔ Read, never imported: `cloud/dp/shell` is a different package.
 *
 * ⚠️ ONE STRUCTURAL DIVERGENCE, inherited from `polarity.ts`'s own: DP holds
 * `const mql = window.matchMedia(...)` at MODULE scope and calls `applyToDOM` as an import-time
 * side effect, so the module throws in any environment without a DOM and no test can control the
 * OS preference. Here the media query is reached through a function on every call, which is what
 * makes the "follows the OS after mount" and "no matchMedia at all" assertions writable.
 */
import { createContext, useCallback, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { ResolvedPolarity, ShellPolarity } from '@zion/sdk-contracts';
import {
  applyPolarityToDom,
  readStoredPolarity,
  resolvePolarity,
  systemPrefersDark,
  writeStoredPolarity,
} from './polarity';

const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Subscribe to OS theme changes for `useSyncExternalStore`.
 *
 * ⛔ MODULE-LEVEL, so its identity is stable — an inline arrow would re-subscribe on every render,
 * which React treats as a store change and resolves as an infinite loop.
 *
 * ⚠️ The capability guard mirrors `systemPrefersDark`'s and is reachable the same way (delete the
 * property). Without it, a `system` preference in an environment lacking `matchMedia` throws during
 * render rather than degrading to the light baseline.
 */
function subscribeToSystemPolarity(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {};
  const query = window.matchMedia(SYSTEM_DARK_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

export interface PolarityState {
  /** What the operator asked for — including `'system'`, which is the default. */
  readonly polarity: ShellPolarity;
  /** What that means right now, given the OS. The value `<html>` carries. */
  readonly resolved: ResolvedPolarity;
  readonly setPolarity: (next: ShellPolarity) => void;
}

/**
 * ⚠️ `useSyncExternalStore` rather than a `useEffect` + `useState` pair, because the OS preference
 * is EXTERNAL state: read during render, it cannot be one commit stale. With an effect, the first
 * paint after an OS theme change carries the previous polarity — a visible flash of the wrong
 * theme, on the path (`system`) that almost every operator is on.
 */
export function usePolarity(): PolarityState {
  const [polarity, setPolarityState] = useState<ShellPolarity>(readStoredPolarity);
  const systemIsDark = useSyncExternalStore(subscribeToSystemPolarity, systemPrefersDark);
  const resolved = resolvePolarity(polarity, systemIsDark);

  // ⛔ In an effect, not during render: writing to `document` while rendering is a side effect React
  // may run twice or discard. Keyed on `resolved` so an OS change re-applies without a click.
  useEffect(() => {
    applyPolarityToDom(resolved);
  }, [resolved]);

  const setPolarity = useCallback((next: ShellPolarity) => {
    setPolarityState(next);
    // Persist eagerly. `writeStoredPolarity` swallows a storage failure by design: the click has
    // already taken effect in memory, and only its survival across a reload is lost.
    writeStoredPolarity(next);
  }, []);

  return { polarity, resolved, setPolarity };
}

/**
 * ⚠️ A DEFAULT VALUE rather than a throwing `null` context, matching the DP original. The toggle is
 * permanent chrome and the provider wraps the whole app, so a missing provider is a wiring mistake
 * that shows up as a control that does nothing — not as a crash on the login page.
 */
const PolarityCtx = createContext<PolarityState>({
  polarity: 'system',
  resolved: 'light',
  setPolarity: () => {},
});

export function PolarityProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <PolarityCtx value={usePolarity()}>{children}</PolarityCtx>;
}

export function usePolarityContext(): PolarityState {
  return useContext(PolarityCtx);
}
